
import {
  Vue, Component, Prop, PropSync, Mixins, Watch,
} from 'vue-property-decorator';
import CustomizeTooltip from '@/features/dashboard/components/CustomizeTooltip.vue';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { scrollToInPage } from '@/features/common/helpers';

@Component({
  components: {
    CustomizeTooltip,
  },
})
export default class WidgetWrap extends Mixins(ResponsivenessMixin) {
  @Prop({ required: true }) title!: string;

  @Prop({ default: '' }) contentClass!: string;

  @Prop({ default: 'big' }) size!: string;

  @Prop({ required: true }) widgetName!: string;

  @Prop({
    default: () => {
      return { width: '140px', maxWidth: '140px' };
    },
  }) positionStyle!: Partial<CSSStyleDeclaration>;

  @PropSync('position', { required: true }) syncedPosition!: number;

  @Prop({ required: true }) totalPositions!: number;

  @Prop({ default: false }) isLoading!: boolean;

  private widgetTransition = 600;

  private show = true;

  private flipped = false;

  private initWidgetHeight = 0;

  get positionItems(): Array<number> {
    const items: Array<number> = [];

    for (let i = 1; i <= this.totalPositions; i += 1) {
      items.push(i);
    }

    return items;
  }

  get widgetWrap(): HTMLElement {
    return this.$refs.widgetWrap as HTMLElement;
  }

  get widget(): HTMLElement {
    return (this.$refs.widget as Vue).$el as HTMLElement;
  }

  get widgetFront(): HTMLElement {
    return this.$refs.widgetFront as HTMLElement;
  }

  get widgetBack(): HTMLElement {
    return this.$refs.widgetBack as HTMLElement;
  }

  get positionOfFirstWidget(): number {
    return this.$store.getters.getPositionOfFirstWidget;
  }

  get isBigWidgetAvailable(): boolean {
    return this.$store.getters.getIsBigWidgetAvailable;
  }

  get isSmallWidgetAvailable(): boolean {
    return this.$store.getters.getIsSmallWidgetAvailable;
  }

  get widgetFirst(): boolean {
    if (this.isBigWidgetAvailable) {
      return this.size === 'big' && this.syncedPosition === this.positionOfFirstWidget;
    }

    if (this.isSmallWidgetAvailable) {
      return this.size === 'small'
        && this.syncedPosition === this.positionOfFirstWidget;
    }

    return false;
  }

  get customizeTooltipStep(): number {
    return this.$store.getters.getCustomizeTooltipStep;
  }

  get showCustomizeTooltip(): boolean {
    return this.$store.getters.getShowCustomizeTooltip;
  }

  get showElipsisTooltip(): boolean {
    return this.showCustomizeTooltip && this.customizeTooltipStep === 2;
  }

  get showMoveTooltip(): boolean {
    return this.showCustomizeTooltip && this.customizeTooltipStep === 1;
  }

  get widgetStyles(): {[key: string]: unknown} {
    return {
      transition: `transform ${this.widgetTransition}ms`,
    };
  }

  @Watch('isMobile')
  private async recalculateInitHeight(): Promise<void> {
    await this.$nextTick();
    this.calculateInitHeight();
  }

  mounted(): void {
    this.calculateInitHeight();
  }

  private calculateInitHeight(): void {
    const minHeight = +getComputedStyle(this.widgetWrap).minHeight.replace('px', '');

    if (minHeight < this.widgetFront.clientHeight) {
      this.widgetWrap.style.height = `${this.widgetFront.clientHeight}px`;
    } else {
      this.widgetFront.style.height = `${this.widgetWrap.clientHeight}px`;
      this.widgetWrap.style.height = `${minHeight}px`;
    }
  }

  private nextStep(): void {
    const step = this.customizeTooltipStep + 1;
    this.$store.commit('setCustomizeTooltipStep', step);

    if (step === 3) scrollToInPage();
  }

  private flip(): void {
    const minHeight = +getComputedStyle(this.widgetWrap).minHeight.replace('px', '');

    this.widget.classList.toggle('flipped');

    if (this.flipped) {
      if (minHeight < this.widgetFront.clientHeight) {
        this.widgetWrap.style.height = `${this.widgetFront.clientHeight}px`;
      } else {
        this.widgetWrap.style.height = `${minHeight}px`;
        this.widgetFront.style.height = `${minHeight}px`;
      }
      this.widgetBack.style.opacity = '0';

      setTimeout(() => {
        this.widgetFront.style.opacity = '1';
      }, this.widgetTransition - this.widgetTransition / 1.5);
    } else if (minHeight < this.widgetBack.clientHeight) {
      this.widgetWrap.style.height = `${this.widgetBack.clientHeight}px`;
      this.widgetFront.style.opacity = '0';

      setTimeout(() => {
        this.widgetBack.style.opacity = '1';
      }, this.widgetTransition - this.widgetTransition / 1.5);
    } else {
      this.widgetWrap.style.height = `${minHeight}px`;
      this.widgetBack.style.height = `${minHeight}px`;
      this.widgetFront.style.opacity = '0';

      setTimeout(() => {
        this.widgetBack.style.opacity = '1';
      }, this.widgetTransition - this.widgetTransition / 1.5);
    }

    this.flipped = !this.flipped;
  }

  private hideWidget() {
    this.$store.dispatch('syncWidgetShow', {
      key: this.widgetName,
      value: false,
    });

    eventBus.$emit(eventNames.UPDATE_DASHBOARD_WIDGETS_COLUMN, {
      method: 'pop',
      widgetName: this.widgetName,
      size: this.size,
    });
  }

  private apply(): void {
    this.$emit('apply', this.widgetName);
    this.flip();
  }
}
